import { useEffect, useRef, useState } from "react";
import { IAddressFieldsInput, IAreaDetailInput, IFieldValidationInput } from "@types";
import parsePhoneNumber from "libphonenumber-js";
import { userApi } from "gate";
import { toast } from "react-toastify";
import { getNotifyConfig } from "@utils/helpers";
import { useTranslations } from "next-intl";
import useAddressFormValue from "./useAddressFormValue";

const SHORT_ADDRESS_FIELD_KEY = "autofill_short_address";
const SHORT_ADDRESS_PATTERN = /^[A-Z]{4}\d{4}$/;

function useAddressForm(d: {
  area: IAreaDetailInput;
  defaultValue?: IAddressFieldsInput;
  id?: number;
  setMapPosition?: (data: { lat: number; lng: number; street: string }) => void;
  setUseMap?: (data: boolean) => void;
  useMap?: boolean;
  onAutofillAreaChange?: (areaId: number) => void;
  onSubmit: (data: IAddressFieldsInput) => void;
}) {
  const t = useTranslations();
  const [isAutofillLoading, setIsAutofillLoading] = useState(false);
  const shortAddressRequestRef = useRef(0);
  const lastShortAddressRef = useRef("");
  const currentShortAddressRef = useRef("");

  const {
    value,
    setValue,
    types,
    setTypes,
    data,
    setData,
    errors,
    setErrors,
    setIsValid,
    setValidations,
    defaultValidations,
    isValid,
    isLoading,
    setIsLoading,
  } = useAddressFormValue(d);

  const { id, setMapPosition, setUseMap, useMap, onAutofillAreaChange, onSubmit } = d;
  const fieldValidation = (_value: any, validations: IFieldValidationInput[]) => {
    const validationList: string[] = [];
    for (const v of validations || []) {
      validateField(_value, v, validationList);
    }
    return validationList;
  };

  const validateField = (_value: any, v: IFieldValidationInput, validationList: string[]) => {
    switch (v.type) {
      case "max-length":
        if (_value && `${_value}`.trim().length > Number(v.value)) {
          validationList.push(v.message);
        }
        break;
      case "min-length":
        if (_value && `${_value}`.trim().length < Number(v.value)) {
          validationList.push(v.message);
        }
        break;
      case "regex":
        if (!new RegExp(v.value, "i").test(String(_value)) && _value) {
          validationList.push(v.message);
        }
        break;
      case "required":
        if (!_value?.toString()?.trim()) {
          validationList.push(v.message);
        }
        break;
      case "phone":
        validatePhoneNumber(_value, v, validationList);
        break;
      default:
        break;
    }
  };

  const validatePhoneNumber = (_value: any, v: IFieldValidationInput, validationList: string[]) => {
    if (_value) {
      const phoneNumber = parsePhoneNumber(_value);
      if (phoneNumber) {
        if (!phoneNumber.isValid()) {
          validationList.push(v.message);
        }
      } else {
        validationList.push(v.message);
      }
    }
  };

  const normalizeShortAddress = (_value: any) => String(_value ?? "").trim().toUpperCase();

  const handleShortAddressAutofill = async (shortAddress: string) => {
    currentShortAddressRef.current = shortAddress;

    if (!SHORT_ADDRESS_PATTERN.test(shortAddress)) {
      shortAddressRequestRef.current += 1;
      lastShortAddressRef.current = "";
      setIsAutofillLoading(false);
      return;
    }

    if (lastShortAddressRef.current === shortAddress) {
      return;
    }

    const requestId = shortAddressRequestRef.current + 1;
    shortAddressRequestRef.current = requestId;
    lastShortAddressRef.current = shortAddress;
    setIsAutofillLoading(true);

    try {
      const res = await userApi.fromShortAddress({ short_address: shortAddress }).fetch();
      if (
        shortAddressRequestRef.current !== requestId ||
        currentShortAddressRef.current !== shortAddress
      ) {
        return;
      }

      const extractedAddress = res?.data?.extracted_address;
      if (!extractedAddress || !Object.keys(extractedAddress).length) {
        toast.error(t("w-shortAddressAutofillFailed"), getNotifyConfig());
        return;
      }

      applyExtractedAddress(extractedAddress);
    } catch (error) {
    } finally {
      if (shortAddressRequestRef.current === requestId) {
        setIsAutofillLoading(false);
      }
    }
  };

  const applyExtractedAddress = (extractedAddress: Record<string, any>) => {
    const extractedValues: Record<string, any> = {};
    const nextErrors: Record<string, string[]> = {};
    let autofillAreaId: number | undefined;
    let autofillMapPosition: { lat: number; lng: number; street: string } | undefined;

    for (const key in extractedAddress) {
      const fieldValue = extractedAddress[key];
      extractedValues[key] = fieldValue;
      nextErrors[key] = fieldValidation(fieldValue, defaultValidations[key] || []);

      if (types[key] === "area") {
        const areaId = Number(fieldValue);
        if (!Number.isNaN(areaId)) {
          autofillAreaId = areaId;
        }
      }

      if (types[key] === "map" && fieldValue) {
        const [lat, lng] = String(fieldValue).split(",").map(Number);
        if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
          autofillMapPosition = {
            lat,
            lng,
            street: data?.fill_street ? String(extractedAddress[data.fill_street] || "") : "",
          };
        }
      }
    }

    setValue((currentValue) => ({ ...currentValue, ...extractedValues }));
    setErrors((currentErrors) => ({ ...currentErrors, ...nextErrors }));

    if (autofillAreaId !== undefined) {
      onAutofillAreaChange?.(autofillAreaId);
    }

    if (autofillMapPosition) {
      setMapPosition?.(autofillMapPosition);
      setUseMap?.(true);
    }
  };

  const handelChangeValue = (
    key: string,
    _value: any,
    validation: IFieldValidationInput[],
    depend?: string,
    reset?: string,
    street?: string,
    newFormValues: { [key: string]: any } = {}
  ) => {
    const nextFieldValue = key === SHORT_ADDRESS_FIELD_KEY ? normalizeShortAddress(_value) : _value;

    if (key === "map" && (data?.fill_street || newFormValues)) {
      const streetPayload = street ? { [data?.fill_street ?? "fill_street"]: street } : {};
      setValue({ ...value, [key]: nextFieldValue, ...streetPayload, ...newFormValues });
      setErrors({ ...errors, [key]: fieldValidation(nextFieldValue, validation) });
    } else if (reset) {
      if (reset === "map") {
        setUseMap?.(false);
      }
      setValue({ ...value, [key]: nextFieldValue, [reset]: "" });
      setErrors({ ...errors, [key]: fieldValidation(nextFieldValue, validation) });
    } else {
      setValue({ ...value, [key]: nextFieldValue });
      setErrors({ ...errors, [key]: fieldValidation(nextFieldValue, validation) });
    }

    if (key === SHORT_ADDRESS_FIELD_KEY) {
      handleShortAddressAutofill(nextFieldValue);
    }
  };

  const handelUseMap = (value: boolean) => {
    setUseMap?.(value);
  };

  useEffect(() => {
    let hasError = false;
    for (const key in value) {
      const e = fieldValidation(value[key], defaultValidations[key]);
      if (e.length) {
        hasError = true;
      }
    }
    setIsValid(!hasError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handelSubmit = async () => {
    const finalItems = prepareFinalItems();
    const hasError = checkForErrors();

    if (!hasError) {
      try {
        setIsLoading(true);
        const res = await submitAddressData(finalItems);
        handleSubmissionSuccess(res);
        setIsLoading(false);
      } catch (error) {
        handleSubmissionError(error);
        setIsLoading(false);
      }
    }
  };

  function prepareFinalItems() {
    const finalItems: { key: string; value: any }[] = [];
    for (const key in value) {
      if (types[key] !== "phone") {
        finalItems.push({
          key,
          value: types[key] === "map" && !useMap ? undefined : String(value[key]).trim(),
        });
      } else if (types[key] === "phone" && value[key]) {
        finalItems.push({ key, value: String(value[key]) });
      }
    }
    return finalItems;
  }

  function checkForErrors() {
    let hasError = false;
    const errors: Record<string, string[]> = {};
    for (const key in value) {
      const e = fieldValidation(value[key], defaultValidations[key]);
      errors[key] = e;
      if (e.length) {
        hasError = true;
      }
    }
    setErrors(errors);
    setIsValid(!hasError);
    return hasError;
  }

  async function submitAddressData(finalItems: { key: string; value: any }[]) {
    let res;
    if (id) {
      res = await userApi.editDynamicAddress({ items: finalItems }, id).fetch();
    } else {
      res = await userApi.addDynamicAddress({ items: finalItems }).fetch();
    }
    return res;
  }

  function handleSubmissionSuccess(res: any) {
    onSubmit(res.data);
    toast.success(
      id ? t("w-updateAddressSuccessMsg") : t("w-addAddressSuccessMsg"),
      getNotifyConfig()
    );
  }

  function handleSubmissionError(error: any) {
    console.log(error);
  }

  return {
    isLoading,
    isAutofillLoading,
    setIsLoading,
    value,
    setValue,
    types,
    setTypes,
    defaultValidations,
    setValidations,
    data,
    setData,
    isValid,
    setIsValid,
    handelUseMap,
    handelChangeValue,
    fieldValidation,
    errors,
    setErrors,
    handelSubmit,
  };
}

export default useAddressForm;
